import React, { useState, FormEvent } from 'react';
import { useLang } from '@shootout-comps/i18n';
import PasswordInput from '../components/PasswordInput';

interface ResetPasswordPageProps {
  token: string;
  onDone: () => void;
}

export default function ResetPasswordPage({ token, onDone }: ResetPasswordPageProps) {
  const { t } = useLang();
  const [newPw,    setNewPw]    = useState('');
  const [confPw,   setConfPw]   = useState('');
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState<{ type: 'ok'|'err'; text: string } | null>(null);
  const [done,     setDone]     = useState(false);

  const pwStrength = () => {
    if (!newPw) return null;
    const s = (newPw.length >= 12 ? 1 : 0) + (/[A-Z]/.test(newPw) ? 1 : 0) + (/[0-9]/.test(newPw) ? 1 : 0) + (/[^A-Za-z0-9]/.test(newPw) ? 1 : 0);
    if (s <= 1) return { label: t.pwStrengthWeak,   color: '#e74c3c', w: '25%' };
    if (s === 2) return { label: t.pwStrengthFair,   color: '#e67e22', w: '50%' };
    if (s === 3) return { label: t.pwStrengthGood,   color: '#f1c40f', w: '75%' };
    return             { label: t.pwStrengthStrong, color: '#2ecc71', w: '100%' };
  };
  const str = pwStrength();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (newPw !== confPw) { setMsg({ type: 'err', text: 'Passwords do not match.' }); return; }
    if (newPw.length < 8)  { setMsg({ type: 'err', text: t.passwordMinLength }); return; }
    setSaving(true); setMsg(null);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: newPw }),
      });
      const data = await res.json() as { message?: string; error?: string };
      if (!res.ok) { setMsg({ type: 'err', text: data.error ?? 'Failed' }); }
      else { setMsg({ type: 'ok', text: data.message ?? 'Password updated.' }); setDone(true); }
    } catch { setMsg({ type: 'err', text: 'Network error.' }); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={{ width: '100%', maxWidth: '420px', background: 'linear-gradient(135deg,var(--baize),var(--felt))', border: '2px solid rgba(201,168,76,0.35)', borderRadius: '14px', padding: 'clamp(1.5rem,6vw,2.5rem)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>🔑</div>
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--gold)', lineHeight: 1 }}>{t.setNewPassword}</h1>
          {!done && <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginTop: '0.3rem' }}>Choose a strong password for your account.</p>}
        </div>

        {done ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
            <p style={{ color: '#2ecc71', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>{msg?.text}</p>
            <button onClick={() => { window.history.replaceState({}, '', '/'); onDone(); }} style={{ background: 'linear-gradient(135deg,var(--gold),var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.85rem 2rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', width: '100%', minHeight: '48px' }}>
              🏆 Sign In Now
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div>
              <label style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: '0.35rem' }}>{t.newPassword}</label>
              <PasswordInput value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" required
                style={{ width: '100%', padding: '0.72rem 0.9rem', background: 'rgba(0,0,0,0.3)', border: '1.5px solid rgba(201,168,76,0.3)', borderRadius: '8px', color: 'var(--chalk)', outline: 'none', boxSizing: 'border-box' }} />
              {str && (
                <>
                  <div style={{ height: '3px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: str.w, background: str.color, transition: 'all 0.3s' }} />
                  </div>
                  <p style={{ fontSize: '0.68rem', color: str.color, marginTop: '3px' }}>{str.label}</p>
                </>
              )}
            </div>
            <div>
              <label style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: '0.35rem' }}>{t.confirmNewPassword}</label>
              <PasswordInput value={confPw} onChange={e => setConfPw(e.target.value)} autoComplete="new-password" required
                style={{ width: '100%', padding: '0.72rem 0.9rem', background: 'rgba(0,0,0,0.3)', border: `1.5px solid ${confPw && confPw !== newPw ? '#e74c3c' : confPw && confPw === newPw ? '#2ecc71' : 'rgba(201,168,76,0.3)'}`, borderRadius: '8px', color: 'var(--chalk)', outline: 'none', boxSizing: 'border-box' }} />
            </div>

            {msg && (
              <div style={{ background: msg.type === 'ok' ? 'rgba(46,204,113,0.12)' : 'rgba(192,57,43,0.18)', border: `1px solid ${msg.type === 'ok' ? 'rgba(46,204,113,0.35)' : 'rgba(192,57,43,0.4)'}`, borderRadius: '6px', padding: '0.55rem 0.85rem', color: msg.type === 'ok' ? '#2ecc71' : '#e74c3c', fontSize: '0.82rem' }}>
                {msg.text}
              </div>
            )}

            <button type="submit" disabled={saving || !newPw || !confPw} style={{ background: 'linear-gradient(135deg,var(--gold),var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.9rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', opacity: saving || !newPw || !confPw ? 0.5 : 1, minHeight: '48px', marginTop: '0.25rem' }}>
              {saving ? 'Saving…' : t.setNewPassword}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
